import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import {
  useGetCurrentUserQuery,
  useUpdateUserProfileMutation,
} from "../../../api/users";
import Radio from "../../../components/forms/Radio";
import { RadioContainer } from "../questionnaireInputs/Radio";
import { Card } from "../../../components/layout/Card";
import {
  ErrorWrapper,
  InputWrapper,
} from "../questionnaireInputs/SharedFormStyles";
import RequiredStar from "../../../components/RequiredAsterisk";
import { Gender } from "../../../../../api/src/types";
import { StyledDescription } from "../Questionnaire";
import { CardContainer } from "../Question";

const DescriptionContainer = styled.div`
  margin-top: 16px;
`;

const userGenderQuestionConfig = {
  title: "What are your preferred pronouns?",
  isRequired: true,
  description: "We need to know how to reference you in your legal document.",
};

const userGenderOptions = [
  {
    label: "he/him/his",
    value: Gender.MALE,
  },
  {
    label: "she/her/hers",
    value: Gender.FEMALE,
  },
  {
    label: "they/them/theirs",
    value: Gender.NON_BINARY,
  },
];

const UserGender = ({
  shouldValidateQuestionnaireSection = false,
}: {
  shouldValidateQuestionnaireSection?: boolean;
}) => {
  const { data: user } = useGetCurrentUserQuery();
  const [updateUser, { /*isLoading,*/ isError, reset }] =
    useUpdateUserProfileMutation();

  const [selectedOption, setSelectedOption] = useState<string | null>(
    user?.gender ?? null,
  );

  /* id of the option whose save is in-flight */
  //const [pendingId, setPendingId] = useState<string | null>(null);
  const [errorId, setErrorId] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setSelectedOption(user.gender ?? null);
    }
  }, [user]);

  const handleDropdownChange = async (value: string) => {
    reset();
    setSelectedOption(value);
    setErrorId(null);
    //setPendingId(value);

    if (!user?.id) return;

    try {
      await updateUser({
        id: user.id,
        gender: value as Gender,
      });
    } catch (error) {
      console.error("Error updating user gender:", error);
      setErrorId(value);
    } finally {
      //setPendingId(null);
    }
  };

  const validationError = !!(
    selectedOption === null && shouldValidateQuestionnaireSection
  );

  return (
    <CardContainer>
      <Card
        header={{
          title: (
            <>
              {userGenderQuestionConfig.title}
              {userGenderQuestionConfig.isRequired && <RequiredStar />}
            </>
          ),
        }}
        isError={validationError}
      >
        {/* Question description */}
        <DescriptionContainer>
          {userGenderQuestionConfig.description && (
            <StyledDescription
              dangerouslySetInnerHTML={{
                __html: userGenderQuestionConfig.description,
              }}
            />
          )}
        </DescriptionContainer>

        {userGenderOptions.map((option) => {
          //const isOptionLoading = pendingId === option.value && isLoading;
          const hasError = errorId === option.value && isError;

          return (
            <React.Fragment key={option.value}>
              <InputWrapper>
                <RadioContainer>
                  <Radio
                    label={option.label}
                    id={option.value}
                    isChecked={selectedOption === option.value}
                    onToggle={() => {
                      void handleDropdownChange(option.value);
                    }}
                  />
                  {/* Commenting out the loader and success icon for now as per the request of the design team */}
                  {/*
                  {isOptionLoading && (
                    <IconContainer>
                      <Loader spinnerOnly={true} size={isMobile ? 16 : 34} />
                    </IconContainer>
                  )}
                  */}
                </RadioContainer>
              </InputWrapper>
              {hasError && (
                <ErrorWrapper>
                  Error saving answer, please refresh the page and try again
                </ErrorWrapper>
              )}
            </React.Fragment>
          );
        })}
      </Card>
    </CardContainer>
  );
};

export default UserGender;
